import React, { useState } from 'react';
import { useSnackbar } from 'notistack';
import { isAxiosError } from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Search, Upload } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import DocumentCard from '@/components/shared/DocumentCard';
import { Skeleton } from '@/components/ui/skeleton';
import { useConfirm } from '@/hooks/useConfirm';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    downloadDocument,
    useCustomerDocuments,
    useDeleteDocument,
    useUploadDocument,
    viewDocument,
} from '@/api/documents/use-documents';
import { CustomerDocument } from '@/models/customer';

interface DocumentsTabProps {
    customerId: string;
}

interface DocumentFormData {
    type: 'id_proof' | 'address_proof' | 'income_proof';
    name: string;
}

const initialDocumentForm: DocumentFormData = {
    type: 'id_proof',
    name: '',
};

const ALLOWED_DOC_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.bmp'];

const DocumentsTab: React.FC<DocumentsTabProps> = ({ customerId }) => {
    const { enqueueSnackbar } = useSnackbar();
    const confirm = useConfirm();
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [formData, setFormData] = useState<DocumentFormData>(initialDocumentForm);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [searchQuery, setSearchQuery] = useState('');

    // Fetch documents using the hook
    const { data: documentsResponse, isLoading, isError } = useCustomerDocuments(customerId);
    const documents = documentsResponse?.data || [];

    // Upload document mutation
    const uploadDocumentMutation = useUploadDocument(customerId);

    // Delete document mutation
    const deleteDocumentMutation = useDeleteDocument(customerId);

    const getStatusClassName = (status: string) => {
        switch (status.toLowerCase()) {
            case 'verified':
                return 'bg-success/10 text-success border-success/20';
            case 'pending':
                return 'bg-warning/10 text-warning border-warning/20';
            case 'rejected':
                return 'bg-destructive/10 text-destructive border-destructive/20';
            default:
                return '';
        }
    };

    const getDocumentTypeLabel = (type: string) => {
        switch (type) {
            case 'id_proof':
                return 'ID Proof';
            case 'address_proof':
                return 'Address Proof';
            case 'income_proof':
                return 'Income Proof';
            default:
                return type;
        }
    };

    // Handle opening the upload dialog
    const handleOpenUploadDialog = () => {
        setFormData(initialDocumentForm);
        setFormErrors({});
        setSelectedFile(null);
        setIsAddDialogOpen(true);
    };

    // Handle closing the upload dialog
    const handleCloseUploadDialog = () => {
        setIsAddDialogOpen(false);
    };

    // Handle form input changes
    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name) {
            setFormData(prev => ({ ...prev, [name]: value }));

            // Clear error when field is edited
            if (formErrors[name]) {
                setFormErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[name];
                    return newErrors;
                });
            }
        }
    };

    // Handle select changes
    const handleSelectChange = (value: string) => {
        setFormData(prev => ({ ...prev, type: value as DocumentFormData['type'] }));
    };

    // Handle file selection
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            const lower = file.name.toLowerCase();
            const allowed = ALLOWED_DOC_EXTENSIONS.some(ext => lower.endsWith(ext));
            if (!allowed) {
                enqueueSnackbar('Only PDF, JPEG, PNG, or BMP files are allowed.', {
                    variant: 'error',
                });
                e.target.value = '';
                return;
            }
            setSelectedFile(file);

            // Auto-fill the name field with the file name (without extension)
            const fileName = file.name.split('.').slice(0, -1).join('.');
            setFormData(prev => ({ ...prev, name: fileName || file.name }));

            // Clear file error if it exists
            if (formErrors.file) {
                setFormErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.file;
                    return newErrors;
                });
            }
        }
    };

    // Validate the form
    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};

        if (!formData.name) {
            errors.name = 'Document name is required';
        }

        if (!selectedFile) {
            errors.file = 'Please select a file to upload';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle document upload
    const handleUploadDocument = async () => {
        if (!validateForm() || !selectedFile) {
            return;
        }

        try {
            await uploadDocumentMutation.mutateAsync({
                name: formData.name,
                type: formData.type,
                document: selectedFile,
            });
            enqueueSnackbar('Document uploaded successfully', { variant: 'success' });
            handleCloseUploadDialog();
        } catch (error: unknown) {
            console.error('Error uploading document:', error);
            let message = 'Upload failed. Please try again.';
            if (isAxiosError(error)) {
                const data = error.response?.data as {
                    errors?: Record<string, string[] | string>;
                    message?: string;
                };
                const errs = data?.errors;
                if (errs && typeof errs === 'object') {
                    const flat = Object.values(errs).flatMap(v => (Array.isArray(v) ? v : [v]));
                    const first = flat.find(
                        (v): v is string => typeof v === 'string' && v.length > 0
                    );
                    if (first) {
                        message = first;
                    }
                }
                if (
                    message === 'Upload failed. Please try again.' &&
                    typeof data?.message === 'string'
                ) {
                    message = data.message;
                }
            }
            enqueueSnackbar(message, { variant: 'error' });
        }
    };

    // Handle document deletion
    const handleDeleteDocument = async (documentId: string) => {
        const ok = await confirm({
            title: 'Delete document?',
            description:
                'Are you sure you want to delete this document? This action cannot be undone.',
            confirmLabel: 'Delete',
            variant: 'destructive',
        });
        if (!ok) return;

        try {
            await deleteDocumentMutation.mutateAsync(documentId);
        } catch (error) {
            console.error('Error deleting document:', error);
        }
    };

    // Handle document download
    const handleDownloadDocument = async (document: CustomerDocument) => {
        try {
            await downloadDocument(customerId, document.id);
        } catch (error) {
            console.error('Error downloading document:', error);
            enqueueSnackbar('Failed to download document. Please try again.', {
                variant: 'error',
            });
        }
    };

    // Handle document view
    const handleViewDocument = async (document: CustomerDocument) => {
        try {
            await viewDocument(customerId, document.id);
        } catch (error) {
            console.error('Error viewing document:', error);
            enqueueSnackbar('Failed to open document. Please try again.', { variant: 'error' });
        }
    };

    // Filter documents based on search query
    const filteredDocuments = documents.filter(
        doc =>
            doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            doc.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
            doc.status.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">Customer Documents</h2>
                <Button
                    onClick={handleOpenUploadDialog}
                    className="flex items-center gap-2"
                    disabled={uploadDocumentMutation.isPending}
                >
                    <Plus className="h-4 w-4" />
                    Upload Document
                </Button>
            </div>

            <div className="flex items-center space-x-2">
                <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                        type="search"
                        placeholder="Search documents..."
                        className="pl-8"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                    />
                </div>
            </div>

            {isLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <Card key={i}>
                            <CardContent className="p-4 space-y-3">
                                <Skeleton className="h-5 w-32" />
                                <Skeleton className="h-4 w-24" />
                                <Skeleton className="h-8 w-full rounded-md" />
                            </CardContent>
                        </Card>
                    ))}
                </div>
            ) : isError ? (
                <Card>
                    <CardContent className="p-6 text-center">
                        <p className="text-destructive">
                            Error loading documents. Please try again.
                        </p>
                    </CardContent>
                </Card>
            ) : filteredDocuments.length === 0 ? (
                <Card>
                    <CardContent className="p-6 text-center">
                        <p className="text-muted-foreground mb-4">No documents found</p>
                        <Button
                            variant="outline"
                            onClick={handleOpenUploadDialog}
                            className="flex items-center gap-2"
                        >
                            <Plus className="h-4 w-4" />
                            Upload First Document
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredDocuments.map(doc => (
                        <DocumentCard
                            key={doc.id}
                            document={doc}
                            onView={handleViewDocument}
                            onDownload={handleDownloadDocument}
                            onDelete={documentId => handleDeleteDocument(String(documentId))}
                            showDelete={true}
                            isDeleting={deleteDocumentMutation.isPending}
                            getDocumentTypeLabel={getDocumentTypeLabel}
                            getStatusClassName={getStatusClassName}
                        />
                    ))}
                </div>
            )}

            {/* Upload Document Dialog */}
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Upload New Document</DialogTitle>
                    </DialogHeader>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div className="space-y-2">
                            <Label htmlFor="type">Document Type</Label>
                            <Select value={formData.type} onValueChange={handleSelectChange}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select document type" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="id_proof">ID Proof</SelectItem>
                                    <SelectItem value="address_proof">Address Proof</SelectItem>
                                    <SelectItem value="income_proof">Income Proof</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="name">Document Name *</Label>
                            <Input
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleFormChange}
                                className={formErrors.name ? 'border-destructive' : ''}
                                required
                            />
                            {formErrors.name && (
                                <p className="text-sm text-destructive">{formErrors.name}</p>
                            )}
                        </div>

                        <div className="col-span-full space-y-2">
                            <Label htmlFor="document-file">File Upload *</Label>
                            <div className="border-2 border-dashed border-border rounded-lg p-6">
                                <input
                                    type="file"
                                    id="document-file"
                                    className="hidden"
                                    accept=".pdf,.jpg,.jpeg,.png,.bmp,application/pdf,image/jpeg,image/png,image/bmp"
                                    onChange={handleFileChange}
                                />
                                <label htmlFor="document-file" className="cursor-pointer">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        className="w-full flex items-center gap-2"
                                        asChild
                                    >
                                        <span>
                                            <Upload className="h-4 w-4" />
                                            {selectedFile ? 'Change File' : 'Select File'}
                                        </span>
                                    </Button>
                                </label>
                                {selectedFile && (
                                    <p className="text-sm text-muted-foreground mt-2">
                                        Selected file: {selectedFile.name} (
                                        {Math.round(selectedFile.size / 1024)} KB)
                                    </p>
                                )}
                                {formErrors.file && (
                                    <p className="text-sm text-destructive mt-2">
                                        {formErrors.file}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    <DialogFooter className="mt-6">
                        <Button variant="outline" onClick={handleCloseUploadDialog}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleUploadDocument}
                            disabled={uploadDocumentMutation.isPending}
                        >
                            {uploadDocumentMutation.isPending ? 'Uploading...' : 'Upload Document'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default DocumentsTab;
